import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";

import "./SecondDepositBonusUSDT.css";


/* =========================================================
   COLLECTIONS
========================================================= */

const USDT_COLLECTION =
  "usdt_transactions";

const USERS_COLLECTION =
  "users";


/* =========================================================
   CURRENCY
========================================================= */

const USDT_CURRENCY =
  "USDT";


/*
  IMPORTANT:
  Correct Firestore user balance field:

  usdtBalance

  NOT:

  USDTBalance
*/

const USDT_BALANCE_FIELD =
  "usdtBalance";


/* =========================================================
   SECOND DEPOSIT SETTINGS
========================================================= */

const SECOND_DEPOSIT_BONUS_PERCENT =
  5;


/* =========================================================
   MINIMUM SECOND DEPOSIT
========================================================= */

const SECOND_DEPOSIT_MINIMUM_USDT =
  10;


/* =========================================================
   NORMALIZE USERNAME
========================================================= */

const normalizeUsername = (
  username
) => {

  return String(
    username || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   NORMALIZE STATUS
========================================================= */

const normalizeStatus = (
  status
) => {

  return String(
    status || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   NORMALIZE POSITION
========================================================= */

const normalizePosition = (
  position
) => {

  return String(
    position || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   FORMAT USDT
========================================================= */

const formatUSDT = (
  amount
) => {

  const numericAmount =
    Number(
      amount || 0
    );


  return `${numericAmount.toLocaleString(
    undefined,
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }
  )} USDT`;

};


/* =========================================================
   TIMESTAMP → SECONDS
========================================================= */

const getTimestampSeconds = (
  item
) => {

  const value =
    item?.createdAt ||
    item?.timestamp ||
    item?.paidAt ||
    item?.updatedAt;


  if (
    !value
  ) {

    return 0;

  }


  /*
    Firestore Timestamp
  */

  if (
    typeof value?.seconds ===
    "number"
  ) {

    return Number(
      value.seconds
    );

  }


  /*
    Firestore Timestamp
    with toDate()
  */

  if (
    typeof value?.toDate ===
    "function"
  ) {

    const date =
      value.toDate();


    if (
      date instanceof Date &&
      !Number.isNaN(
        date.getTime()
      )
    ) {

      return Math.floor(
        date.getTime() /
        1000
      );

    }

  }


  /*
    Numeric timestamp
  */

  if (
    typeof value ===
    "number"
  ) {

    return value >
      100000000000

      ? Math.floor(
          value / 1000
        )

      : Math.floor(
          value
        );

  }


  /*
    String date
  */

  const parsed =
    new Date(
      value
    ).getTime();


  if (
    Number.isNaN(
      parsed
    )
  ) {

    return 0;

  }


  return Math.floor(
    parsed /
    1000
  );

};


/* =========================================================
   FIND USER BY USERNAME
========================================================= */

const findUserByUsername = async (
  username
) => {

  if (
    !username
  ) {

    return null;

  }


  const usersRef =
    collection(
      db,
      USERS_COLLECTION
    );


  /*
    ---------------------------------------------------------
    EXACT SEARCH
    ---------------------------------------------------------
  */

  try {

    const exactQuery =
      query(

        usersRef,

        where(
          "username",
          "==",
          username
        )

      );


    const exactSnapshot =
      await getDocs(
        exactQuery
      );


    if (
      !exactSnapshot.empty
    ) {

      return exactSnapshot.docs[0];

    }

  }

  catch (
    error
  ) {

    console.warn(
      "Exact username search failed:",
      error
    );

  }


  /*
    ---------------------------------------------------------
    CASE-INSENSITIVE FALLBACK
    ---------------------------------------------------------
  */

  const allUsers =
    await getDocs(
      usersRef
    );


  const normalized =
    normalizeUsername(
      username
    );


  return (

    allUsers.docs.find(
      userDoc =>

        normalizeUsername(
          userDoc.data()?.username
        ) ===
        normalized

    ) || null

  );

};


/* =========================================================
   GET DEPOSIT POSITION
========================================================= */

const getDepositPosition = (
  deposit
) => {

  return normalizePosition(
    deposit?.position ||
    deposit?.depositOrderType ||
    ""
  );

};


/* =========================================================
   GET SECOND DEPOSIT
========================================================= */

const getSecondDeposit =
  async (
    username
  ) => {

    if (
      !username
    ) {

      return null;

    }


    const depositsQuery =
      query(

        collection(
          db,
          USDT_COLLECTION
        ),

        where(
          "username",
          "==",
          username
        ),

        where(
          "status",
          "==",
          "success"
        )

      );


    const snapshot =
      await getDocs(
        depositsQuery
      );


    /*
      -------------------------------------------------------
      NORMALIZE DEPOSITS
      -------------------------------------------------------
    */

    const deposits =
      snapshot.docs.map(
        depositDoc => {

          const data =
            depositDoc.data();


          return {

            id:
              depositDoc.id,

            ...data,

            source:
              USDT_COLLECTION,

            currency:
              USDT_CURRENCY,

            sourceCurrency:
              USDT_CURRENCY,

            originalAmount:
              Number(
                data.amount || 0
              ),

          };

        }
      );


    /*
      -------------------------------------------------------
      VALID DEPOSITS
      -------------------------------------------------------
    */

    const validDeposits =
      deposits.filter(
        deposit => {

          return (

            normalizeUsername(
              deposit.username
            ) ===
            normalizeUsername(
              username
            ) &&

            normalizeStatus(
              deposit.status
            ) ===
            "success" &&

            Number(
              deposit.originalAmount ||
              0
            ) > 0

          );

        }
      );


    /*
      -------------------------------------------------------
      SORT OLD → NEW
      -------------------------------------------------------
    */

    validDeposits.sort(
      (
        a,
        b
      ) => {

        const timeA =
          getTimestampSeconds(
            a
          );


        const timeB =
          getTimestampSeconds(
            b
          );


        if (
          timeA ===
          timeB
        ) {

          return String(
            a.id
          ).localeCompare(
            String(
              b.id
            )
          );

        }


        return (
          timeA -
          timeB
        );

      }
    );


    /*
      -------------------------------------------------------
      EXPLICIT SECOND
      -------------------------------------------------------
    */

    const explicitSecond =
      validDeposits.find(
        deposit =>

          getDepositPosition(
            deposit
          ) ===
          "second"

      );


    if (
      explicitSecond
    ) {

      return explicitSecond;

    }


    /*
      -------------------------------------------------------
      FALLBACK:
      SECOND SUCCESSFUL DEPOSIT
      -------------------------------------------------------
    */

    if (
      validDeposits.length >=
      2
    ) {

      return validDeposits[1];

    }


    return null;

  };


/* =========================================================
   SECOND DEPOSIT BONUS CALCULATOR
========================================================= */

const getSecondDepositBonusData = (
  amount
) => {

  const depositAmount =
    Number(
      amount || 0
    );


  /*
    ---------------------------------------------------------
    MINIMUM CHECK
    ---------------------------------------------------------
  */

  if (
    depositAmount <
    SECOND_DEPOSIT_MINIMUM_USDT
  ) {

    return {

      locked:
        true,

      percent:
        0,

      amount:
        0,

      label:
        `Locked - Min ${SECOND_DEPOSIT_MINIMUM_USDT} USDT`,

      bonusType:
        "second",

    };

  }


  /*
    ---------------------------------------------------------
    CALCULATE BONUS
    ---------------------------------------------------------
  */

  const bonusAmount =
    depositAmount *
    (
      SECOND_DEPOSIT_BONUS_PERCENT /
      100
    );


  return {

    locked:
      false,

    percent:
      SECOND_DEPOSIT_BONUS_PERCENT,

    amount:
      Number(
        bonusAmount.toFixed(8)
      ),

    label:
      `Second Deposit ${SECOND_DEPOSIT_BONUS_PERCENT}%`,

    bonusType:
      "second",

  };

};


/* =========================================================
   COMPONENT
========================================================= */

export default function SecondDepositBonusUSDT() {

  const navigate =
    useNavigate();


  /* =======================================================
     USERNAME
  ======================================================= */

  const [
    username,
    setUsername,
  ] = useState("");


  /* =======================================================
     DEPOSIT
  ======================================================= */

  const [
    deposit,
    setDeposit,
  ] = useState(null);


  /* =======================================================
     BONUS
  ======================================================= */

  const [
    bonus,
    setBonus,
  ] = useState(0);


  const [
    bonusPercent,
    setBonusPercent,
  ] = useState(0);


  /* =======================================================
     DEPOSIT AMOUNT
  ======================================================= */

  const [
    depositAmount,
    setDepositAmount,
  ] = useState(0);


  /* =======================================================
     LOADING
  ======================================================= */

  const [
    loading,
    setLoading,
  ] = useState(true);


  /* =======================================================
     APPLYING
  ======================================================= */

  const [
    applying,
    setApplying,
  ] = useState(false);


  /* =======================================================
     COMPLETED
  ======================================================= */

  const [
    completed,
    setCompleted,
  ] = useState(false);


  /* =======================================================
     MESSAGE
  ======================================================= */

  const [
    message,
    setMessage,
  ] = useState("");


  /* =======================================================
     LOAD SECOND DEPOSIT BONUS
  ======================================================= */

  useEffect(
    () => {

      let mounted =
        true;


      const loadBonus =
        async () => {

          try {

            setLoading(
              true
            );


            /*
              ------------------------------------------------
              GET LOGGED-IN USERNAME
              ------------------------------------------------
            */

            const storedUsername =
              localStorage.getItem(
                "username"
              );


            if (
              !storedUsername
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "Login required."
                );

              }

              return;

            }


            if (
              mounted
            ) {

              setUsername(
                storedUsername
              );

            }


            /*
              ------------------------------------------------
              FIND USER
              ------------------------------------------------
            */

            const userDoc =
              await findUserByUsername(
                storedUsername
              );


            if (
              !userDoc
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "Account not found."
                );

              }

              return;

            }


            /*
              ------------------------------------------------
              FIND SECOND DEPOSIT
              ------------------------------------------------
            */

            const secondDeposit =
              await getSecondDeposit(
                storedUsername
              );


            if (
              !secondDeposit
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "Second successful deposit haijapatikana."
                );

              }

              return;

            }


            /*
              ------------------------------------------------
              VERIFY POSITION
              ------------------------------------------------
            */

            const position =
              getDepositPosition(
                secondDeposit
              );


            /*
              If position exists,
              it must be second.
            */

            if (
              position &&
              position !==
              "second"
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "Deposit hii si Second Deposit."
                );

              }

              return;

            }


            /*
              ------------------------------------------------
              DOUBLE CLAIM CHECK
              ------------------------------------------------
            */

            if (
              secondDeposit.selfBonusGiven ===
              true
            ) {

              if (
                mounted
              ) {

                setDeposit(
                  secondDeposit
                );

                setDepositAmount(
                  Number(
                    secondDeposit.amount ||
                    secondDeposit.originalAmount ||
                    0
                  )
                );

                setCompleted(
                  true
                );

                setMessage(
                  "Second Deposit Bonus tayari imetolewa."
                );

              }

              return;

            }


            /*
              ------------------------------------------------
              ADMIN BONUS CHECK
              ------------------------------------------------
            */

            if (
              secondDeposit.adminBonusGiven ===
              true
            ) {

              if (
                mounted
              ) {

                setDeposit(
                  secondDeposit
                );

                setCompleted(
                  true
                );

                setMessage(
                  "Second Deposit Bonus tayari imelipwa."
                );

              }

              return;

            }


            /*
              ------------------------------------------------
              AMOUNT
              ------------------------------------------------
            */

            const amount =
              Number(
                secondDeposit.amount ||
                secondDeposit.originalAmount ||
                0
              );


            if (
              amount <=
              0
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "Second deposit amount haijapatikana."
                );

              }

              return;

            }


            /*
              ------------------------------------------------
              MINIMUM
              ------------------------------------------------
            */

            if (
              amount <
              SECOND_DEPOSIT_MINIMUM_USDT
            ) {

              if (
                mounted
              ) {

                setDeposit(
                  secondDeposit
                );

                setDepositAmount(
                  amount
                );

                setMessage(
                  `Second deposit yako lazima iwe angalau ${SECOND_DEPOSIT_MINIMUM_USDT} USDT.`
                );

              }

              return;

            }


            /*
              ------------------------------------------------
              CALCULATE BONUS
              ------------------------------------------------
            */

            const bonusData =
              getSecondDepositBonusData(
                amount
              );


            if (
              bonusData.locked ||
              bonusData.amount <=
              0
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "Second Deposit Bonus haijafuzu."
                );

              }

              return;

            }


            /*
              ------------------------------------------------
              SET UI
              ------------------------------------------------
            */

            if (
              mounted
            ) {

              setDeposit(
                secondDeposit
              );

              setDepositAmount(
                amount
              );

              setBonus(
                bonusData.amount
              );

              setBonusPercent(
                bonusData.percent
              );

              setMessage(
                ""
              );

            }

          }

          catch (
            error
          ) {

            console.error(
              "Second Deposit Bonus loading error:",
              error
            );


            if (
              mounted
            ) {

              setMessage(
                error?.message ||
                "Unable to load Second Deposit Bonus."
              );

            }

          }

          finally {

            if (
              mounted
            ) {

              setLoading(
                false
              );

            }

          }

        };


      loadBonus();


      return () => {

        mounted =
          false;

      };

    },
    []
  );


  /* =======================================================
     APPLY SECOND DEPOSIT BONUS
  ======================================================= */

  const applyBonus =
    async () => {

      if (
        applying ||
        completed ||
        !bonus ||
        !username
      ) {

        return;

      }


      setApplying(
        true
      );


      setMessage(
        ""
      );


      try {

        /*
          ------------------------------------------------
          FIND USER
          ------------------------------------------------
        */

        const userDoc =
          await findUserByUsername(
            username
          );


        if (
          !userDoc
        ) {

          throw new Error(
            "User account not found."
          );

        }


        /*
          ------------------------------------------------
          USE CURRENT DEPOSIT
          ------------------------------------------------
        */

        let selectedDeposit =
          deposit;


        /*
          If state is empty,
          fetch again.
        */

        if (
          !selectedDeposit
        ) {

          selectedDeposit =
            await getSecondDeposit(
              username
            );

        }


        if (
          !selectedDeposit
        ) {

          throw new Error(
            "Second deposit not found."
          );

        }


        /*
          ------------------------------------------------
          DEPOSIT REFERENCE
          ------------------------------------------------
        */

        const depositRef =
          doc(
            db,
            USDT_COLLECTION,
            selectedDeposit.id
          );


        /*
          ------------------------------------------------
          FINAL BONUS
          ------------------------------------------------
        */

        let finalBonus =
          0;


        /*
          =================================================
          FIRESTORE ATOMIC TRANSACTION
          =================================================
        */

        await runTransaction(
          db,
          async (
            transaction
          ) => {

            /*
              ===============================================
              READ DEPOSIT FIRST
              ===============================================
            */

            const depositSnapshot =
              await transaction.get(
                depositRef
              );


            if (
              !depositSnapshot.exists()
            ) {

              throw new Error(
                "Second deposit transaction not found."
              );

            }


            /*
              ===============================================
              FRESH DEPOSIT DATA
              ===============================================
            */

            const freshDeposit =
              depositSnapshot.data();


            /*
              ===============================================
              SUCCESS STATUS
              ===============================================
            */

            if (
              normalizeStatus(
                freshDeposit.status
              ) !==
              "success"
            ) {

              throw new Error(
                "Deposit is not successful."
              );

            }


            /*
              ===============================================
              USERNAME CHECK
              ===============================================
            */

            const depositUsername =
              normalizeUsername(
                freshDeposit.username
              );


            const currentUsername =
              normalizeUsername(
                username
              );


            if (
              depositUsername &&
              depositUsername !==
              currentUsername
            ) {

              throw new Error(
                "Deposit username haiendani na account hii."
              );

            }


            /*
              ===============================================
              POSITION
              ===============================================
            */

            const freshPosition =
              getDepositPosition(
                freshDeposit
              );


            if (
              freshPosition &&
              freshPosition !==
              "second"
            ) {

              throw new Error(
                "Deposit hii si Second Deposit."
              );

            }


            /*
              ===============================================
              DOUBLE CLAIM
              ===============================================
            */

            if (
              freshDeposit.selfBonusGiven ===
              true
            ) {

              throw new Error(
                "Second Deposit Bonus tayari imetolewa."
              );

            }


            /*
              ===============================================
              ADMIN BONUS PROTECTION
              ===============================================
            */

            if (
              freshDeposit.adminBonusGiven ===
              true
            ) {

              throw new Error(
                "Second Deposit Bonus tayari imelipwa."
              );

            }


            /*
              ===============================================
              READ USER
              ===============================================
            */

            const userSnapshot =
              await transaction.get(
                userDoc.ref
              );


            if (
              !userSnapshot.exists()
            ) {

              throw new Error(
                "User account not found."
              );

            }


            /*
              ===============================================
              USER DATA
              ===============================================
            */

            const userData =
              userSnapshot.data();


            /*
              ===============================================
              IMPORTANT BALANCE FIELD
              ===============================================

              EXACT FIELD:

              usdtBalance

              NOT:

              USDTBalance
            */

            const currentBalance =
              Number(
                userData[
                  USDT_BALANCE_FIELD
                ] || 0
              );


            /*
              ===============================================
              DEPOSIT AMOUNT
              ===============================================
            */

            const amount =
              Number(
                freshDeposit.amount ||
                0
              );


            if (
              amount <=
              0
            ) {

              throw new Error(
                "Second deposit amount haijapatikana."
              );

            }


            /*
              ===============================================
              MINIMUM SECOND DEPOSIT
              ===============================================
            */

            if (
              amount <
              SECOND_DEPOSIT_MINIMUM_USDT
            ) {

              throw new Error(
                `Second deposit lazima iwe angalau ${SECOND_DEPOSIT_MINIMUM_USDT} USDT.`
              );

            }


            /*
              ===============================================
              CALCULATE BONUS
              ===============================================
            */

            const bonusData =
              getSecondDepositBonusData(
                amount
              );


            if (
              bonusData.locked ||
              bonusData.amount <=
              0
            ) {

              throw new Error(
                "Second Deposit Bonus haijafuzu."
              );

            }


            const calculatedBonus =
              Number(
                bonusData.amount
              );


            /*
              ===============================================
              NEW BALANCE
              ===============================================
            */

            const newBalance =
              currentBalance +
              calculatedBonus;


            /*
              ===============================================
              UPDATE USER
              ===============================================

              ONLY CORRECT FIELD:

              usdtBalance
            */

            transaction.update(
              userDoc.ref,
              {

                [USDT_BALANCE_FIELD]:
                  newBalance,

              }
            );


            /*
              ===============================================
              BONUS DATA
              ===============================================
            */

            const depositUpdate = {

              /*
                ---------------------------------------------
                BONUS STATUS
                ---------------------------------------------
              */

              selfBonusGiven:
                true,

              adminBonusGiven:
                true,

              selfBonusClaimedAmount:
                calculatedBonus,

              selfBonusAmount:
                calculatedBonus,

              selfBonusCurrency:
                USDT_CURRENCY,

              selfBonusPercent:
                Number(
                  bonusData.percent ||
                  0
                ),

              selfBonusLabel:
                bonusData.label,

              selfBonusType:
                "second",

              selfBonusLocked:
                false,

              selfBonusApprovedBy:
                "user",

              selfBonusApprovedAt:
                serverTimestamp(),


              /*
                ---------------------------------------------
                DEPOSIT INFORMATION
                ---------------------------------------------
              */

              originalDepositAmount:
                amount,

              sourceCurrency:
                USDT_CURRENCY,

              bonusDepositAmount:
                amount,

              bonusDepositCurrency:
                USDT_CURRENCY,

              bonusDepositUSDTEquivalent:
                amount,

              /*
                IMPORTANT:
                Balance field saved exactly
                as usdtBalance.
              */

              bonusBalanceField:
                USDT_BALANCE_FIELD,

              bonusCurrency:
                USDT_CURRENCY,

              bonusStatus:
                "credited",

              bonusTransactionType:
                "depositBonus",


              /*
                ---------------------------------------------
                SECOND DEPOSIT
                ---------------------------------------------
              */

              bonusDepositOrder:
                "second",

              depositOrderType:
                "second",

              position:
                "second",


              /*
                ---------------------------------------------
                SECOND DEPOSIT BONUS
                ---------------------------------------------
              */

              secondDepositBonusGiven:
                true,

              secondDepositBonusAmount:
                calculatedBonus,

              secondDepositBonusCurrency:
                USDT_CURRENCY,

              secondDepositBonusPercent:
                Number(
                  bonusData.percent ||
                  0
                ),

              secondDepositBonusStatus:
                "credited",

              secondDepositBonusClaimedAt:
                serverTimestamp(),

            };


            /*
              ===============================================
              UPDATE DEPOSIT
              ===============================================
            */

            transaction.update(
              depositRef,
              depositUpdate
            );


            /*
              ===============================================
              FINAL UI VALUE
              ===============================================
            */

            finalBonus =
              calculatedBonus;

          }
        );


        /*
          =====================================================
          SUCCESS
          =====================================================
        */

        setCompleted(
          true
        );


        setBonus(
          finalBonus
        );


        setMessage(
          `Hongera! ${formatUSDT(
            finalBonus
          )} imeongezwa kwenye usdtBalance yako.`
        );


        console.log(
          "SECOND DEPOSIT USDT BONUS COMPLETED",
          {

            username,

            secondDepositBonus:
              finalBonus,

            currency:
              USDT_CURRENCY,

            balanceField:
              USDT_BALANCE_FIELD,

          }
        );

      }

      catch (
        error
      ) {

        console.error(
          "Second Deposit Bonus payment failed:",
          error
        );


        setMessage(
          error?.message ||
          "Bonus payment failed."
        );

      }

      finally {

        setApplying(
          false
        );

      }

    };


  /* =======================================================
     LOADING SCREEN
  ======================================================= */

  if (
    loading
  ) {

    return (

      <div className="sdb-page">

        <button
          type="button"
          className="sdb-back-btn"
          onClick={() =>
            navigate(-1)
          }
        >

          <span className="sdb-back-icon">
            ←
          </span>

          <span>
            Back
          </span>

        </button>


        <div
          className="
            sdb-card
            sdb-loading
          "
        >

          <div className="sdb-loader" />


          <p>
            Checking your second deposit bonus...
          </p>

        </div>

      </div>

    );

  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <div className="sdb-page">


      {/* ===================================================
          BACK BUTTON
      =================================================== */}

      <button
        type="button"
        className="sdb-back-btn"
        onClick={() =>
          navigate(-1)
        }
        aria-label="Go back"
      >

        <span className="sdb-back-icon">
          ←
        </span>

        <span>
          Back
        </span>

      </button>


      {/* ===================================================
          CARD
      =================================================== */}

      <div className="sdb-card">


        {/* =================================================
            ICON
        ================================================= */}

        <div className="sdb-gift">
          🎁
        </div>


        {/* =================================================
            BADGE
        ================================================= */}

        <div className="sdb-badge">
          SECOND DEPOSIT
        </div>


        {/* =================================================
            TITLE
        ================================================= */}

        <h2>
          Second Deposit Bonus
        </h2>


        {/* =================================================
            SUBTITLE
        ================================================= */}

        <p className="sdb-subtitle">

          Hongera{" "}

          <strong>
            {username || "Trader"}
          </strong>

          !

          <br />

          Umefuzu kupata
          Second Deposit Bonus.

        </p>


        {/* =================================================
            DEPOSIT BOX
        ================================================= */}

        {depositAmount > 0 && (

          <div
            className="
              sdb-deposit-box
            "
          >

            <span>
              SECOND DEPOSIT
            </span>


            <strong>
              {formatUSDT(
                depositAmount
              )}
            </strong>


            <small>
              Minimum:
              {" "}
              {SECOND_DEPOSIT_MINIMUM_USDT}
              {" "}
              USDT
            </small>

          </div>

        )}


        {/* =================================================
            BONUS BOX
        ================================================= */}

        {bonus > 0 && (

          <div
            className="
              sdb-bonus-box
            "
          >

            <span>
              YOUR BONUS
            </span>


            <strong>

              {formatUSDT(
                bonus
              )}

            </strong>


            <small>

              Second Deposit Bonus{" "}

              {bonusPercent}%

            </small>

          </div>

        )}


        {/* =================================================
            MESSAGE
        ================================================= */}

        {message && (

          <div
            className={`
              sdb-message
              ${
                completed
                  ? "sdb-success"
                  : "sdb-info"
              }
            `}
          >

            <span>

              {
                completed
                  ? "✓"
                  : "!"
              }

            </span>


            <p>
              {message}
            </p>

          </div>

        )}


        {/* =================================================
            APPLY BUTTON
        ================================================= */}

        {bonus > 0 &&
        !completed && (

          <button
            type="button"
            className="
              sdb-apply-btn
            "
            onClick={
              applyBonus
            }
            disabled={
              applying
            }
          >

            {applying ? (

              <>

                <span
                  className="
                    sdb-button-loader
                  "
                />

                Processing...

              </>

            ) : (

              <>

                APPLY BONUS NOW

                <span>
                  →
                </span>

              </>

            )}

          </button>

        )}


        {/* =================================================
            ALREADY CLAIMED
        ================================================= */}

        {completed && (

          <div
            className="
              sdb-paid
            "
          >

            <span>
              ✓
            </span>

            Bonus Already Claimed

          </div>

        )}


        {/* =================================================
            NOTE
        ================================================= */}

        <p
          className="
            sdb-note
          "
        >

          Bonus yako ni{" "}

          <strong>
            {SECOND_DEPOSIT_BONUS_PERCENT}%
          </strong>

          {" "}ya Second Deposit yako ya USDT.

          <br />

          Minimum Second Deposit ni{" "}

          <strong>
            {SECOND_DEPOSIT_MINIMUM_USDT} USDT
          </strong>

          .

          <br />

          Bonus itaongezwa moja kwa moja kwenye{" "}

          <strong>
            usdtBalance
          </strong>

          {" "}yako.

        </p>


      </div>

    </div>

  );

}
